import React, { createContext, useContext, useState, ReactNode } from 'react';
import { submitFormToNocoDB } from '../../../services/nocodbService';
const baseUrl = import.meta.env.VITE_API_BASE_URL;

export interface ProductService {
  name: string;
  description: string;
  price: string;
}

export interface FormDataType {
  // Seção 1: A Identidade da Sua Empresa
  businessName: string;
  businessIndustry: string;
  businessDescription: string;
  targetAudience: string;
  businessDifferentials: string;

  // Seção 2: A Personalidade do Assistente Virtual
  assistantName: string;
  toneOfVoice: string[];
  emojiUsage: string;
  clientAddressing: string;

  // Seção 3: Conhecimento Essencial
  productsServices: ProductService[];
  workingHours: string;
  businessLocation: string;
  googleMapsLink: string;
  phoneContact: string;
  emailContact: string;
  websiteUrl: string;
  socialMedia: string;
  paymentMethods: string;
  deliveryAreas: string;
  deliveryFee: string;
  deliveryTime: string;

  // Seção 4: Objetivos e Ações
  mainObjective: string;
  faqs: Array<{ question: string; answer: string }>;
  leadCapture: {
    name: boolean;
    phone: boolean;
    email: boolean;
    interest: boolean;
    other: string;
  };
  appointmentTool: string;

  // Seção 5: Limites e Escalonamento
  forbiddenTopics: string;
  transferConditions: string[];
  transferMessage: string;
}

export type ErrorsType = {
  [key in keyof FormDataType]?: string;
} & {
  productsServices?: string;
  faqs?: string;
};

// Status da submissão do formulário
export type SubmissionStatus = 'idle' | 'submitting' | 'success' | 'error';

interface FormContextType {
  formData: FormDataType;
  errors: ErrorsType;
  submissionStatus: SubmissionStatus;
  submissionError: string | null;
  setFormData: React.Dispatch<React.SetStateAction<FormDataType>>;
  setErrors: React.Dispatch<React.SetStateAction<ErrorsType>>;
  validateStep: (step: number) => boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (e: { target: { name: string; value: unknown } }) => void;
  handleMultiSelectChange: (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleProductServiceChange: (index: number, field: keyof ProductService, value: string) => void;
  addProductService: () => void;
  removeProductService: (index: number) => void;
  handleFaqChange: (index: number, field: 'question' | 'answer', value: string) => void;
  addFaq: () => void;
  removeFaq: (index: number) => void;
  resetErrors: () => void;
  submitForm: () => Promise<boolean>;
  resetSubmission: () => void;
}

const FormContext = createContext<FormContextType | undefined>(undefined);

export const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
};

interface FormProviderProps {
  children: ReactNode;
}

export const FormProvider: React.FC<FormProviderProps> = ({ children }) => {
  const [errors, setErrors] = useState<ErrorsType>({});
  const [submissionStatus, setSubmissionStatus] = useState<SubmissionStatus>('idle');
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormDataType>({
    // Seção 1: A Identidade da Sua Empresa
    businessName: '',
    businessIndustry: '',
    businessDescription: '',
    targetAudience: '',
    businessDifferentials: '',

    // Seção 2: A Personalidade do Assistente Virtual
    assistantName: '',
    toneOfVoice: [],
    emojiUsage: '',
    clientAddressing: '',

    // Seção 3: Conhecimento Essencial
    productsServices: [{ name: '', description: '', price: '' }],
    workingHours: '',
    businessLocation: '',
    googleMapsLink: '',
    phoneContact: '',
    emailContact: '',
    websiteUrl: '',
    socialMedia: '',
    paymentMethods: '',
    deliveryAreas: '',
    deliveryFee: '',
    deliveryTime: '',

    // Seção 4: Objetivos e Ações
    mainObjective: '',
    faqs: [{ question: '', answer: '' }],
    leadCapture: {
      name: false,
      phone: false,
      email: false,
      interest: false,
      other: ''
    },
    appointmentTool: '',

    // Seção 5: Limites e Escalonamento
    forbiddenTopics: '',
    transferConditions: [],
    transferMessage: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSelectChange = (e: { target: { name: string; value: unknown } }) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleMultiSelectChange = (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    if (checked) {
      setFormData({
        ...formData,
        [name]: [...(formData[name as keyof FormDataType] as string[]), value]
      });
    } else {
      setFormData({
        ...formData,
        [name]: (formData[name as keyof FormDataType] as string[]).filter(item => item !== value)
      });
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...(formData[parent as keyof FormDataType] as object),
          [child]: checked
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: checked
      });
    }
  };

  const handleProductServiceChange = (index: number, field: keyof ProductService, value: string) => {
    const updatedProducts = [...formData.productsServices];
    updatedProducts[index] = { ...updatedProducts[index], [field]: value };
    setFormData({
      ...formData,
      productsServices: updatedProducts
    });
  };

  const addProductService = () => {
    setFormData({
      ...formData,
      productsServices: [...formData.productsServices, { name: '', description: '', price: '' }]
    });
  };

  const removeProductService = (index: number) => {
    const updatedProducts = [...formData.productsServices];
    updatedProducts.splice(index, 1);
    setFormData({
      ...formData,
      productsServices: updatedProducts
    });
  };

  const handleFaqChange = (index: number, field: 'question' | 'answer', value: string) => {
    const updatedFaqs = [...formData.faqs];
    updatedFaqs[index] = { ...updatedFaqs[index], [field]: value };
    setFormData({
      ...formData,
      faqs: updatedFaqs
    });
  };

  const addFaq = () => {
    setFormData({
      ...formData,
      faqs: [...formData.faqs, { question: '', answer: '' }]
    });
  };

  const removeFaq = (index: number) => {
    const updatedFaqs = [...formData.faqs];
    updatedFaqs.splice(index, 1);
    setFormData({
      ...formData,
      faqs: updatedFaqs
    });
  };

  const resetErrors = () => {
    setErrors({});
  };

  // Função para submeter o formulário para o NocoDB
  const submitForm = async (): Promise<boolean> => {
    // Validar todo o formulário antes de submeter
    const isStep0Valid = validateStep(0);
    const isStep1Valid = validateStep(1);
    const isStep2Valid = validateStep(2);
    const isStep3Valid = validateStep(3);
    const isStep4Valid = validateStep(4);

    if (!isStep0Valid || !isStep1Valid || !isStep2Valid || !isStep3Valid || !isStep4Valid) {
      return false;
    }

    try {
      setSubmissionStatus('submitting');
      setSubmissionError(null);

      // Enviar dados para o NocoDB através do serviço
      await submitFormToNocoDB(formData);

      setSubmissionStatus('success');
            // Envia mensagem após novo registro
      fetch(`${baseUrl}/send-message-to-owner`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mensagem: `Um novo formulário foi preenchido.\n\nhttps://nextalk.cabanalink.com/admin/login`,
        })
      }).catch(error => {
        console.error("Erro ao enviar mensagem:", error);
      });
      return true;
    } catch (error: any) {
      setSubmissionStatus('error');
      setSubmissionError(error.message || 'Ocorreu um erro ao enviar o formulário. Por favor, tente novamente.');
      console.error('Erro ao submeter formulário:', error);
      return false;
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: ErrorsType = {};
    let isValid = true;

    if (step === 0) {
      // Validar Seção 1: A Identidade da Sua Empresa
      if (!formData.businessName.trim()) {
        newErrors.businessName = "Nome da empresa é obrigatório";
        isValid = false;
      }
      if (!formData.businessIndustry.trim()) {
        newErrors.businessIndustry = "Ramo de atividade é obrigatório";
        isValid = false;
      }
      if (!formData.businessDescription.trim()) {
        newErrors.businessDescription = "Descrição do negócio é obrigatória";
        isValid = false;
      }
      if (!formData.businessDifferentials.trim()) {
        newErrors.businessDifferentials = "Diferenciais da empresa são obrigatórios";
        isValid = false;
      }
    }
    else if (step === 1) {
      // Validar Seção 2: A Personalidade do Assistente Virtual
      if (formData.toneOfVoice.length === 0) {
        newErrors.toneOfVoice = "Escolha pelo menos um tom de voz";
        isValid = false;
      }
      if (!formData.emojiUsage) {
        newErrors.emojiUsage = "Escolha uma opção para uso de emojis";
        isValid = false;
      }
      if (!formData.clientAddressing) {
        newErrors.clientAddressing = "Escolha como o assistente deve se dirigir ao cliente";
        isValid = false;
      }
    }
    else if (step === 2) {
      // Validar Seção 3: Conhecimento Essencial
      if (formData.productsServices.length === 0) {
        newErrors.productsServices = "Adicione pelo menos um produto ou serviço";
        isValid = false;
      } else {
        const hasIncompleteProduct = formData.productsServices.some(
          product => !product.name.trim() || !product.description.trim() || !product.price.trim()
        );
        if (hasIncompleteProduct) {
          newErrors.productsServices = "Complete todos os campos de produto/serviço";
          isValid = false;
        }
      }
      if (!formData.workingHours.trim()) {
        newErrors.workingHours = "Horário de funcionamento é obrigatório";
        isValid = false;
      }
    }
    else if (step === 3) {
      // Validar Seção 4: Objetivos e Ações
      if (!formData.mainObjective) {
        newErrors.mainObjective = "Escolha um objetivo principal para o assistente";
        isValid = false;
      }
      if (formData.faqs.length === 0) {
        newErrors.faqs = "Adicione pelo menos uma pergunta frequente";
        isValid = false;
      } else {
        const hasIncompleteFaq = formData.faqs.some(
          faq => !faq.question.trim() || !faq.answer.trim()
        );
        if (hasIncompleteFaq) {
          newErrors.faqs = "Complete todas as perguntas e respostas";
          isValid = false;
        }
      }
    }
    else if (step === 4) {
      // Validar Seção 5: Limites e Escalonamento
      if (formData.transferConditions.length === 0) {
        newErrors.transferConditions = "Selecione pelo menos uma condição para transferência";
        isValid = false;
      }
      if (!formData.transferMessage.trim()) {
        newErrors.transferMessage = "Mensagem de transferência é obrigatória";
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const resetSubmission = () => {
    setSubmissionStatus('idle');
    setSubmissionError(null);
  };

  const value = {
    formData,
    errors,
    submissionStatus,
    submissionError,
    setFormData,
    setErrors,
    validateStep,
    handleChange,
    handleSelectChange,
    handleMultiSelectChange,
    handleCheckboxChange,
    handleProductServiceChange,
    addProductService,
    removeProductService,
    handleFaqChange,
    addFaq,
    removeFaq,
    resetErrors,
    submitForm,
    resetSubmission
  };

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
};